import { ChangeEvent, useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select, { type SelectOption } from "../../../components/ui/Select";
import { useToast } from "../../../contexts/ToastContext";
import { useListPropertiesQuery } from "../../../store/api/propertiesApi";
import { useUpdateUserMutation } from "../../../store/api/usersApi";
import type { User, UserRole } from "../../../types";

interface EditUserModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	user: User | null;
}

const ROLE_OPTIONS: SelectOption[] = [
	{ value: "admin", label: "Administrator" },
	{ value: "tenant", label: "Tenant" },
	{ value: "technician", label: "Technician" },
	{ value: "agent", label: "Sales Agent" },
];

const EditUserModal: React.FC<EditUserModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
	user,
}) => {
	const { success, error: showError } = useToast();
	const [updateUser, { isLoading }] = useUpdateUserMutation();

	// Fetch properties for tenant assignment
	const { data: propertiesData } = useListPropertiesQuery(
		{ page_size: 100 },
		{ skip: user?.role !== "tenant" },
	);

	const [formData, setFormData] = useState({
		// Basic fields
		email: "",
		first_name: "",
		last_name: "",
		phone: "",
		role: "" as UserRole | "",
		is_active: true,
		// Tenant fields
		property_id: "",
		property_address: "",
		unit_number: "",
		emergency_contact_name: "",
		emergency_contact_phone: "",
		// Technician fields
		primary_specialty: "",
		experience: "",
		license_number: "",
		service_area: "",
		// Agent fields
		brokerage_name: "",
		service_areas: "",
		// Admin fields
		company_name: "",
		job_title: "",
		company_address: "",
		property_count: "",
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	// Load user data when modal opens
	useEffect(() => {
		if (isOpen && user) {
			setFormData({
				email: user.email || "",
				first_name: user.first_name || "",
				last_name: user.last_name || "",
				phone: user.phone || "",
				role: user.role || "",
				is_active: user.is_active ?? true,
				property_id: "",
				property_address: user.property_address || "",
				unit_number: user.unit_number || "",
				emergency_contact_name: user.emergency_contact_name || "",
				emergency_contact_phone: user.emergency_contact_phone || "",
				primary_specialty: user.primary_specialty || "",
				experience: "",
				license_number: "",
				service_area: "",
				brokerage_name: "",
				service_areas: "",
				company_name: "",
				job_title: "",
				company_address: "",
				property_count: "",
			});
			setErrors({});
		}
	}, [isOpen, user]);

	const handleInputChange = (field: string, value: string | boolean) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.email.trim()) {
			newErrors.email = "Email is required";
		} else if (!/\S+@\S+\.\S+/.test(formData.email)) {
			newErrors.email = "Please enter a valid email address";
		}

		if (!formData.first_name.trim()) {
			newErrors.first_name = "First name is required";
		}

		if (!formData.last_name.trim()) {
			newErrors.last_name = "Last name is required";
		}

		if (!formData.phone.trim()) {
			newErrors.phone = "Phone number is required";
		}

		if (!formData.role) {
			newErrors.role = "Please select a role";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validate() || !user) {
			return;
		}

		setErrors({});

		try {
			// Prepare update data
			const updateData: any = {
				email: formData.email,
				first_name: formData.first_name,
				last_name: formData.last_name,
				phone: formData.phone,
				role: formData.role,
				is_active: formData.is_active,
			};

			// Add role-specific fields
			if (formData.role === "tenant") {
				if (formData.property_address)
					updateData.property_address = formData.property_address;
				if (formData.unit_number) updateData.unit_number = formData.unit_number;
				if (formData.emergency_contact_name)
					updateData.emergency_contact_name = formData.emergency_contact_name;
				if (formData.emergency_contact_phone)
					updateData.emergency_contact_phone = formData.emergency_contact_phone;
			} else if (formData.role === "technician") {
				if (formData.primary_specialty)
					updateData.primary_specialty = formData.primary_specialty;
				if (formData.experience) updateData.experience = formData.experience;
				if (formData.license_number)
					updateData.license_number = formData.license_number;
				if (formData.service_area)
					updateData.service_area = formData.service_area;
			} else if (formData.role === "agent") {
				if (formData.brokerage_name)
					updateData.brokerage_name = formData.brokerage_name;
				if (formData.service_areas)
					updateData.service_areas = formData.service_areas;
			} else if (formData.role === "admin") {
				if (formData.company_name)
					updateData.company_name = formData.company_name;
				if (formData.job_title) updateData.job_title = formData.job_title;
				if (formData.company_address)
					updateData.company_address = formData.company_address;
				if (formData.property_count)
					updateData.property_count = formData.property_count;
			}

			await updateUser({
				id: user.id,
				data: updateData,
			}).unwrap();

			success("User updated successfully!");
			onSuccess();
		} catch (error: any) {
			console.error("User update error:", error);

			if (error?.data?.error) {
				showError(error.data.error);
				setErrors({ submit: error.data.error });
			} else if (error?.data?.email) {
				const errorMessage = Array.isArray(error.data.email)
					? error.data.email[0]
					: error.data.email;
				setErrors({ email: errorMessage });
				showError(errorMessage);
			} else if (error?.data?.first_name) {
				const errorMessage = Array.isArray(error.data.first_name)
					? error.data.first_name[0]
					: error.data.first_name;
				setErrors({ first_name: errorMessage });
				showError(errorMessage);
			} else if (error?.data?.last_name) {
				const errorMessage = Array.isArray(error.data.last_name)
					? error.data.last_name[0]
					: error.data.last_name;
				setErrors({ last_name: errorMessage });
				showError(errorMessage);
			} else {
				const errorMessage =
					error?.data?.message || "Failed to update user. Please try again.";
				setErrors({ submit: errorMessage });
				showError(errorMessage);
			}
		}
	};

	if (!isOpen || !user) return null;

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
			<div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto modal-shadow">
				{/* Header */}
				<div className="p-6 border-b border-border sticky top-0 bg-card z-10">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Edit User
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								Update user information and settings
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							iconName="X"
						/>
					</div>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} className="p-6 space-y-6">
					{/* Basic Information */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-foreground">
							Basic Information
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Input
								label="Email Address"
								type="email"
								value={formData.email}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									handleInputChange("email", e.target.value)
								}
								error={errors.email}
								required
							/>

							<Input
								label="Phone Number"
								type="tel"
								value={formData.phone}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									handleInputChange("phone", e.target.value)
								}
								error={errors.phone}
								required
							/>

							<Input
								label="First Name"
								type="text"
								value={formData.first_name}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									handleInputChange("first_name", e.target.value)
								}
								error={errors.first_name}
								required
							/>

							<Input
								label="Last Name"
								type="text"
								value={formData.last_name}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									handleInputChange("last_name", e.target.value)
								}
								error={errors.last_name}
								required
							/>

							<div className="md:col-span-2">
								<label className="mb-2 block text-sm font-medium text-foreground">
									Role <span className="text-destructive">*</span>
								</label>
								<Select
									value={formData.role}
									options={ROLE_OPTIONS}
									onValueChange={(value) => {
										handleInputChange("role", value as UserRole);
										setErrors((prev) => ({ ...prev, role: "" }));
									}}
									required
								/>
								{errors.role && (
									<p className="text-sm text-destructive mt-1">{errors.role}</p>
								)}
							</div>

							<div className="md:col-span-2">
								<label className="flex items-center space-x-2">
									<input
										type="checkbox"
										checked={formData.is_active}
										onChange={(e) =>
											handleInputChange("is_active", e.target.checked)
										}
										className="rounded border-border"
									/>
									<span className="text-sm font-medium text-foreground">
										Active Account
									</span>
								</label>
							</div>
						</div>
					</div>

					{/* Role-Specific Fields */}
					{formData.role && (
						<div className="space-y-4 pt-6 border-t border-border">
							<h3 className="text-lg font-medium text-foreground">
								{formData.role.charAt(0).toUpperCase() + formData.role.slice(1)}{" "}
								Details
							</h3>

							{formData.role === "tenant" && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<Input
										label="Unit Number"
										type="text"
										value={formData.unit_number}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("unit_number", e.target.value)
										}
										description="Optional"
									/>
									<Input
										label="Property Address"
										type="text"
										value={formData.property_address}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("property_address", e.target.value)
										}
										description="Optional"
										className="md:col-span-2"
									/>
									<Input
										label="Emergency Contact Name"
										type="text"
										value={formData.emergency_contact_name}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange(
												"emergency_contact_name",
												e.target.value,
											)
										}
										description="Optional"
									/>
									<Input
										label="Emergency Contact Phone"
										type="tel"
										value={formData.emergency_contact_phone}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange(
												"emergency_contact_phone",
												e.target.value,
											)
										}
										description="Optional"
									/>
								</div>
							)}

							{formData.role === "technician" && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<Input
										label="Primary Specialty"
										type="text"
										value={formData.primary_specialty}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("primary_specialty", e.target.value)
										}
										description="Optional"
									/>
									<Input
										label="Experience"
										type="text"
										value={formData.experience}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("experience", e.target.value)
										}
										description="Optional (e.g., '5 years')"
									/>
									<Input
										label="License Number"
										type="text"
										value={formData.license_number}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("license_number", e.target.value)
										}
										description="Optional"
									/>
									<Input
										label="Service Area"
										type="text"
										value={formData.service_area}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("service_area", e.target.value)
										}
										description="Optional"
									/>
								</div>
							)}

							{formData.role === "agent" && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<Input
										label="Brokerage Name"
										type="text"
										value={formData.brokerage_name}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("brokerage_name", e.target.value)
										}
										description="Optional"
									/>
									<Input
										label="Service Areas"
										type="text"
										value={formData.service_areas}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("service_areas", e.target.value)
										}
										description="Optional"
										className="md:col-span-2"
									/>
								</div>
							)}

							{formData.role === "admin" && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<Input
										label="Company Name"
										type="text"
										value={formData.company_name}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("company_name", e.target.value)
										}
										description="Optional"
									/>
									<Input
										label="Job Title"
										type="text"
										value={formData.job_title}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("job_title", e.target.value)
										}
										description="Optional"
									/>
									<Input
										label="Company Address"
										type="text"
										value={formData.company_address}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleInputChange("company_address", e.target.value)
										}
										description="Optional"
										className="md:col-span-2"
									/>
								</div>
							)}
						</div>
					)}

					{errors.submit && (
						<div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
							{errors.submit}
						</div>
					)}

					<div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={isLoading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={isLoading}>
							{isLoading ? (
								<>
									<Icon name="Loader2" size={16} className="mr-2 animate-spin" />
									Saving...
								</>
							) : (
								<>
									<Icon name="Save" size={16} className="mr-2" />
									Save Changes
								</>
							)}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default EditUserModal;
